import localStorageHelper from '@/helper/localStorageHelper';
import { createI18n } from 'petite-vue-i18n';
import { toRaw } from 'vue';
import { usePrimeVue } from 'primevue/config';
import type enLocale from '@/locales/primeVue/en.json';

export enum Locales {
  DE = 'de',
  EN = 'en',
}

const getLocale = () => {
  const savedLocale = localStorageHelper.getLocale();

  // Fallback
  if (!savedLocale) {
    return Locales.DE;
  }

  return savedLocale;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const _i18n = createI18n<[typeof enLocale], 'de' | 'en'>({
  legacy: false,
  locale: getLocale(),
});

const loadPrimeVueLocale = async (locale: Locales) => {
  const primevue = usePrimeVue();

  primevue.config.locale = await (locale === Locales.EN
    ? import('@/locales/primeVue/en.json')
    : import('@/locales/primeVue/de.json'));
};

const setLocale = (locale: Locales) => {
  if (_i18n === null) {
    throw new Error('Tried to change locale before i18n was initialized');
  }

  if (locale.length <= 0) {
    return;
  }

  localStorageHelper.setLocale(locale);
  document.querySelector('html')?.setAttribute('lang', locale);
  _i18n.global.locale = locale;
};

const mergeCurrentLocale = (messageObjects: Record<string, unknown>[]) => {
  const currentLocale = getLocale();
  const localeMessages = toRaw(_i18n?.global.getLocaleMessage(currentLocale));

  const mergedMessages = messageObjects.reduce((previous, current) =>
    Object.assign(current, previous),
  );

  _i18n?.global.mergeLocaleMessage(currentLocale, {
    ...localeMessages,
    ...mergedMessages,
  });
};

export { getLocale, setLocale, mergeCurrentLocale, loadPrimeVueLocale, _i18n };
